import type { AdvisorResponse, AdvisorMode } from "@/lib/api";

export type AdvisorStreamStartEvent = {
  type: "start";
  conversation_id?: number | null;
  mode?: AdvisorMode | string | null;
};

// A step Mae is actually taking ("Checking your saved homes", "Searching
// Morristown", "Reading 43 Pine St"), sent by the backend as the step starts. The
// page shows the latest one until her text begins; nothing is ever shown on a timer.
export type AdvisorStreamStatusEvent = {
  type: "status";
  text: string;
};

export type AdvisorStreamDeltaEvent = {
  type: "delta";
  text: string;
};

export type AdvisorStreamFinalEvent = {
  type: "final";
  response: AdvisorResponse;
  timings?: {
    first_token_ms?: number | null;
    stream_duration_ms?: number | null;
    finalize_ms?: number | null;
    total_request_ms?: number | null;
  };
};

export type AdvisorStreamErrorEvent = {
  type: "error";
  message: string;
};

export type AdvisorStreamEvent =
  | AdvisorStreamStartEvent
  | AdvisorStreamStatusEvent
  | AdvisorStreamDeltaEvent
  | AdvisorStreamFinalEvent
  | AdvisorStreamErrorEvent;

export function parseAdvisorStreamBuffer(buffer: string): { events: AdvisorStreamEvent[]; remainder: string } {
  const lines = buffer.split("\n");
  const remainder = lines.pop() ?? "";
  const events: AdvisorStreamEvent[] = [];
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    try {
      const parsed = JSON.parse(line) as AdvisorStreamEvent;
      if (parsed && typeof parsed === "object" && typeof parsed.type === "string") {
        events.push(parsed);
      }
    } catch {
      continue;
    }
  }
  return { events, remainder };
}

// ── one reply, as its stream arrives ───────────────────────────────────────────

export const CUT_OFF_NOTICE = "Mae's answer was cut off. Try again.";

// What the page shows for the reply being streamed. Text streams in as it is
// written; the home cards, the reply check's notes and the chips arrive only with
// the final event (`response`), below the text. Before the first text, `status` is
// the step in progress, from a status event, never from a timer.
export type AdvisorStreamState = {
  text: string;
  status: string | null;
  streaming: boolean;
  response: AdvisorResponse | null;
  cutOff: boolean;
};

export const initialAdvisorStreamState: AdvisorStreamState = { text: "", status: null, streaming: true, response: null, cutOff: false };

export function applyAdvisorStreamEvent(state: AdvisorStreamState, event: AdvisorStreamEvent): AdvisorStreamState {
  if (state.response || state.cutOff) return state;
  switch (event.type) {
    case "status":
      // Once her text is showing, it has replaced the status line for good.
      return state.text ? state : { ...state, status: event.text };
    case "delta":
      return { ...state, text: `${state.text}${event.text}`, status: null };
    case "final":
      return { ...state, text: event.response.answer, status: null, streaming: false, response: event.response };
    case "error":
      return endAdvisorStream(state);
    default:
      return state;
  }
}

// The stream stopped: an error event, a dropped connection, or an end with no
// final event. Text that arrived stays, marked cut off; with none, there is
// nothing to keep and the caller falls back to the plain request.
export function endAdvisorStream(state: AdvisorStreamState): AdvisorStreamState {
  if (state.response) return state;
  return { ...state, status: null, streaming: false, cutOff: Boolean(state.text.trim()) };
}
